import Link from "next/link";
import React from "react";

export type PaginationMetadata = {
  totalCount: number;
  pageSize: string | number;
  cursor?: string;
  beforeCursor?: string;
};

type PaginationProps = {
  metadata?: PaginationMetadata;
  currentPage: number;
  pageSize: number;
  onPageChange?: (pageNumber: number) => void;
  getPageHref?: (pageNumber: number) => string;
  className?: string;
};

const Pagination = ({
  metadata,
  currentPage,
  onPageChange,
  getPageHref,
  pageSize,
  className = "",
}: PaginationProps) => {
  if (!metadata || metadata.totalCount <= pageSize) {
    return null;
  }
  const totalPages = Math.max(1, Math.ceil(metadata?.totalCount / pageSize));

  const getPageNumbers = () => {
    if (totalPages <= 1) return [];

    const numbers: (number | string)[] = [];
    const showEllipsis = totalPages > 7;

    if (!showEllipsis) {
      for (let i = 1; i <= totalPages; i++) numbers.push(i);
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) numbers.push(i);
      numbers.push("...");
      numbers.push(totalPages);
    } else if (currentPage >= totalPages - 3) {
      numbers.push(1);
      numbers.push("...");
      for (let i = totalPages - 4; i <= totalPages; i++) numbers.push(i);
    } else {
      numbers.push(1);
      numbers.push("...");
      numbers.push(currentPage - 1);
      numbers.push(currentPage);
      numbers.push(currentPage + 1);
      numbers.push("...");
      numbers.push(totalPages);
    }

    return numbers;
  };

  if (totalPages <= 1) return null;

  const pageNumbers = getPageNumbers();

  const baseBtn =
    "inline-flex items-center justify-center min-w-[40px] h-10 px-3 rounded-full text-sm font-medium transition-all duration-200 border";

  const activeBtn = "bg-spec-brand text-white border-spec-brand shadow-sm";
  const inactiveBtn =
    "bg-white text-spec-muted border-spec-border hover:bg-spec-well hover:border-spec-brand";
  const disabledBtn = "opacity-40 cursor-not-allowed pointer-events-none";

  const renderControl = (
    pageNumber: number,
    content: React.ReactNode,
    options: {
      className: string;
      ariaLabel: string;
      disabled?: boolean;
      current?: boolean;
      rel?: string;
    },
  ) => {
    const classes = `${options.className} ${
      options.disabled ? disabledBtn : ""
    }`;

    if (options.disabled) {
      return (
        <span
          aria-disabled="true"
          aria-label={options.ariaLabel}
          className={classes}
        >
          {content}
        </span>
      );
    }

    if (options.current) {
      return (
        <span
          aria-current="page"
          aria-label={options.ariaLabel}
          className={classes}
        >
          {content}
        </span>
      );
    }

    if (getPageHref) {
      return (
        <Link
          href={getPageHref(pageNumber)}
          rel={options.rel}
          aria-label={options.ariaLabel}
          className={classes}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        type="button"
        onClick={() => onPageChange?.(pageNumber)}
        aria-label={options.ariaLabel}
        className={classes}
      >
        {content}
      </button>
    );
  };

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-2 ${className}`}
    >
      <p className="text-xs font-medium text-spec-muted">
        Showing {(currentPage - 1) * pageSize + 1}–
        {Math.min(currentPage * pageSize, metadata.totalCount)} of{" "}
        {metadata.totalCount.toLocaleString()}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {renderControl(
          currentPage - 1,
          <>
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Prev
          </>,
          {
            className: `${baseBtn} ${inactiveBtn}`,
            ariaLabel: "Previous page",
            disabled: currentPage <= 1,
            rel: "prev",
          },
        )}

        <div className="flex items-center gap-1.5 overflow-x-auto max-w-full py-1">
          {pageNumbers.map((pageNum, idx) =>
            pageNum === "..." ? (
              <span
                key={`ellipsis-${idx}`}
                className="inline-flex items-center justify-center w-10 h-10 text-gray-400 text-sm"
              >
                ...
              </span>
            ) : (
              renderControl(pageNum as number, pageNum, {
                className: `${baseBtn} ${
                  currentPage === pageNum ? activeBtn : inactiveBtn
                }`,
                ariaLabel: `Page ${pageNum}`,
                current: currentPage === pageNum,
              })
            ),
          )}
        </div>

        {renderControl(
          currentPage + 1,
          <>
            Next
            <svg
              className="w-4 h-4 ml-1"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </>,
          {
            className: `${baseBtn} ${inactiveBtn}`,
            ariaLabel: "Next page",
            disabled: currentPage >= totalPages,
            rel: "next",
          },
        )}
      </div>
    </div>
  );
};

export default Pagination;
